import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState } from "@/components/app/ui-bits";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Buildings, ListChecks, Money, Handshake, Coins, Package, ShieldCheck,
  CalendarCheck, ArrowUpRight, ClockCounterClockwise, TrendUp, TrendDown, Wallet,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function Kpi({ label, value, icon: Icon, tone = "slate", money = false, testid }) {
  return (
    <div className="dc-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">{label}</span>
        {Icon && <Icon size={15} className={`text-${tone}-500`} weight="duotone" />}
      </div>
      <div className="mt-1 font-display text-2xl font-bold tabular-nums text-slate-900">
        {money ? formatCurrency(value || 0) : (value ?? 0)}
      </div>
    </div>
  );
}

function Section({ title, action, children, testid }) {
  return (
    <div className="dc-card overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <span className="overline">{title}</span>
        {action}
      </div>
      <div className="max-h-72 overflow-y-auto">{children}</div>
    </div>
  );
}

function Rows({ items, render, empty }) {
  if (!items?.length) return <div className="px-4 py-6 text-center text-xs text-slate-400">{empty}</div>;
  return <ul className="divide-y divide-slate-100 text-sm">{items.map(render)}</ul>;
}

export default function BranchDashboard() {
  const { user } = useAuth();
  const isBranch = user?.role === "branch_user";
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isBranch) api.get("/branches").then((r) => {
      setBranches(r.data.items || []);
      if (r.data.items?.length && !branchId) setBranchId(r.data.items[0].id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBranch]);

  useEffect(() => {
    if (!isBranch && !branchId) return;
    setLoading(true);
    api.get("/branches/my-dashboard", { params: isBranch ? {} : { branch_id: branchId } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [isBranch, branchId]);

  const k = data?.kpis || {};
  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="branch-dashboard">
      <PageHeader
        eyebrow="Branch operations"
        title={data?.branch?.name ? `${data.branch.name} ${data.branch.code ? `· ${data.branch.code}` : ""}` : "Branch Dashboard"}
        description={data?.branch?.location || "Everything scoped to your branch"}
        actions={
          <div className="flex items-center gap-2">
            {!isBranch && branches.length > 0 && (
              <Select value={branchId} onValueChange={setBranchId}>
                <SelectTrigger className="w-52" data-testid="branch-picker"><SelectValue placeholder="Pick a branch" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · {b.code}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Button asChild size="sm" className="btn-primary-blue" data-testid="branch-new-payment">
              <Link to="/payments"><Money size={13} className="mr-1.5" /> New payment request</Link>
            </Button>
          </div>
        }
      />

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">Loading branch data…</div>
      ) : !data ? (
        <EmptyState icon={Buildings} title="No branch data" description="Ask an administrator to assign you to a branch." />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <Kpi label="Projects" value={k.projects} icon={Buildings} testid="kpi-projects" />
            <Kpi label="Open tasks" value={k.tasks_open} icon={ListChecks} testid="kpi-tasks" />
            <Kpi label="Procurement open" value={k.procurement_open} icon={Handshake} testid="kpi-procurement" />
            <Kpi label="Payments pending" value={k.payment_requests_pending} icon={Money} testid="kpi-payments" />
            <Kpi label="Pending approvals" value={k.pending_approvals} icon={ClockCounterClockwise} testid="kpi-approvals" />
            <Kpi label="Branch expenses" value={k.expenses} icon={TrendDown} money testid="kpi-expenses" />
            <Kpi label="Branch revenue" value={k.revenue} icon={TrendUp} money testid="kpi-revenue" />
            <Kpi label="AR outstanding" value={k.ar_outstanding} icon={Coins} money testid="kpi-ar" />
            <Kpi label="AP outstanding" value={k.ap_outstanding} icon={Coins} money testid="kpi-ap" />
            <Kpi label="Petty cash" value={k.petty_cash_balance} icon={Wallet} money testid="kpi-petty" />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Section title={`Branch projects (${data.projects?.length || 0})`} testid="branch-projects">
              <Rows items={data.projects} empty="No projects assigned to this branch yet."
                render={(p) => (
                  <li key={p.id} className="flex items-center justify-between px-4 py-2.5">
                    <div>
                      <div className="font-semibold text-slate-900">{p.name}</div>
                      <div className="text-xs text-slate-500">{p.client || "—"} · budget {formatCurrency(p.budget || 0)}</div>
                    </div>
                    <StatusBadge status={p.status} />
                  </li>
                )} />
            </Section>

            <Section title={`Tasks requiring action (${data.tasks?.length || 0})`} testid="branch-tasks"
              action={<Button asChild size="sm" variant="outline"><Link to="/tasks">Open Tasks <ArrowUpRight size={11} className="ml-1" /></Link></Button>}>
              <Rows items={data.tasks} empty="No open tasks."
                render={(t) => (
                  <li key={t.id} className="flex items-center justify-between px-4 py-2.5">
                    <div>
                      <div className="font-semibold text-slate-900">{t.title}</div>
                      <div className="text-xs text-slate-500">{t.assigned_to_name || "Unassigned"} {t.due_date ? `· due ${formatDate(t.due_date)}` : ""}</div>
                    </div>
                    <StatusBadge status={t.status} />
                  </li>
                )} />
            </Section>

            <Section title={`Payment requests (${data.payment_requests?.length || 0})`} testid="branch-payments"
              action={<Button asChild size="sm" variant="outline"><Link to="/payments">Open <ArrowUpRight size={11} className="ml-1" /></Link></Button>}>
              <Rows items={data.payment_requests} empty="No payment requests yet."
                render={(p) => (
                  <li key={p.id} className="flex items-center justify-between px-4 py-2.5">
                    <div>
                      <div className="font-mono-alt text-xs text-slate-500">{p.request_no}</div>
                      <div className="font-semibold text-slate-900">{p.purpose}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-alt tabular-nums font-semibold">{formatCurrency(p.amount || 0)}</div>
                      <StatusBadge status={p.status} />
                    </div>
                  </li>
                )} />
            </Section>

            <Section title={`Procurement (${data.procurement?.length || 0})`} testid="branch-procurement"
              action={<Button asChild size="sm" variant="outline"><Link to="/procurement">Open <ArrowUpRight size={11} className="ml-1" /></Link></Button>}>
              <Rows items={data.procurement} empty="No open procurement records."
                render={(r) => (
                  <li key={r.id} className="flex items-center justify-between px-4 py-2.5">
                    <div>
                      <div className="font-mono-alt text-xs text-slate-500">{r.ref}</div>
                      <div className="font-semibold text-slate-900">{r.title}</div>
                    </div>
                    <div className="text-right text-xs text-slate-500">{r.phase} · {formatCurrency(r.estimated_value || 0)}</div>
                  </li>
                )} />
            </Section>

            <Section title={`Upcoming appointments & events (${(data.appointments?.length || 0) + (data.events?.length || 0)})`} testid="branch-calendar"
              action={<Button asChild size="sm" variant="outline"><Link to="/calendar"><CalendarCheck size={11} className="mr-1" /> Calendar</Link></Button>}>
              <Rows items={[...(data.appointments || []).map((a) => ({ ...a, _kind: "Appointment", _title: a.subject || a.name })),
                            ...(data.events || []).map((e) => ({ ...e, _kind: "Event", _title: e.title }))]}
                empty="Nothing scheduled."
                render={(x) => (
                  <li key={`${x._kind}-${x.id}`} className="flex items-center justify-between px-4 py-2.5">
                    <div>
                      <div className="font-semibold text-slate-900">{x._title}</div>
                      <div className="text-xs text-slate-500">{x._kind} · {formatDate(x.date)} {x.time || x.start_time || ""}</div>
                    </div>
                  </li>
                )} />
            </Section>

            <Section title={`Branch assets — ${k.assets || 0} total · ${k.assets_assigned || 0} assigned · ${k.assets_due_return || 0} due return`} testid="branch-assets"
              action={<Button asChild size="sm" variant="outline"><Link to="/my-assets"><Package size={11} className="mr-1" /> Requests</Link></Button>}>
              <Rows items={data.assets_due_return} empty="No assets due for return."
                render={(m) => (
                  <li key={m.id} className="flex items-center justify-between px-4 py-2.5">
                    <span className="font-semibold text-slate-900">{m.asset_name}</span>
                    <span className="text-xs text-red-600">due back {formatDate(m.return_date)}</span>
                  </li>
                )} />
            </Section>

            <Section title={`Compliance items (${k.compliance_items || 0})`} testid="branch-compliance">
              <Rows items={data.compliance} empty="No branch compliance items."
                render={(c) => (
                  <li key={c.id} className="flex items-center justify-between px-4 py-2.5">
                    <span className="font-semibold text-slate-900"><ShieldCheck size={12} className="mr-1 inline text-slate-400" />{c.title}</span>
                    <span className="text-xs text-slate-500">{c.expiry_date ? `expires ${formatDate(c.expiry_date)}` : c.status}</span>
                  </li>
                )} />
            </Section>

            <Section title="Recent branch activity" testid="branch-activity">
              <Rows items={data.activity} empty="No recent activity."
                render={(a, i) => (
                  <li key={i} className="px-4 py-2">
                    <span className="font-mono-alt text-[10px] uppercase tracking-wider text-slate-500">{a.action}</span>
                    <div className="text-xs text-slate-600">{a.actor_email} · {a.created_at ? formatDate(a.created_at) : ""}</div>
                  </li>
                )} />
            </Section>
          </div>
        </>
      )}
    </div>
  );
}
